import { useEffect, useRef, useState } from "react";
import { Loader2, LocateFixed, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { loadGoogleMaps, currentPosition, DEFAULT_CENTER } from "@/lib/googleMaps";
import { logger } from "@/lib/logger";
import { toast } from "sonner";

/**
 * LocationPinMap — a map with a fixed pin in the middle. The customer drags
 * the map until the pin sits on their door; onMove({lat, lng}) fires when the
 * map stops moving. `value` recentres the map (e.g. after a search pick).
 * Without a Maps key it shows a short note and only the "current location"
 * button works.
 */
export default function LocationPinMap({ value, onMove, height = 240, testid = "pin-map", hint = "Move the map to put the pin on your door" }) {
  const box = useRef(null);
  const map = useRef(null);
  const last = useRef(value?.lat != null ? value : null);   // last point we know about
  const same = (a, b) => a && b && Math.abs(a.lat - b.lat) < 1e-6 && Math.abs(a.lng - b.lng) < 1e-6;
  const [state, setState] = useState("loading");   // loading | ready | unavailable
  const [locating, setLocating] = useState(false);
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;

  useEffect(() => {
    let alive = true;
    loadGoogleMaps().then(async (g) => {
      if (!alive) return;
      if (!g || !box.current) { setState("unavailable"); return; }
      try {
        const { Map } = await g.maps.importLibrary("maps");
        const start = value?.lat != null ? value : DEFAULT_CENTER;
        map.current = new Map(box.current, {
          center: start,
          zoom: value?.lat != null ? 18 : 12,
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: "greedy",
          clickableIcons: false,
        });
        map.current.addListener("idle", () => {
          const c = map.current.getCenter();
          const p = { lat: c.lat(), lng: c.lng() };
          if (!last.current || same(p, last.current)) { last.current = p; return; }   // initial render / our own recentre
          last.current = p;
          onMoveRef.current && onMoveRef.current(p, "pin");
        });
        setState("ready");
      } catch (e) {
        logger.warn("Map failed to initialise:", e);
        setState("unavailable");
      }
    });
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Recentre when the parent picks a new point (search result / current location).
  useEffect(() => {
    if (value?.lat == null) return;
    const p = { lat: value.lat, lng: value.lng };
    if (same(p, last.current)) return;
    last.current = p;
    if (!map.current) return;
    map.current.setCenter(p);
    if (map.current.getZoom() < 17) map.current.setZoom(18);
  }, [value?.lat, value?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  const useCurrent = async () => {
    setLocating(true);
    try {
      const p = await currentPosition();
      last.current = p;
      onMoveRef.current && onMoveRef.current(p, "current_location");
      if (map.current) { map.current.setCenter(p); map.current.setZoom(18); }
    } catch (e) {
      toast.error(e.message);
    } finally { setLocating(false); }
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="relative rounded-xl overflow-hidden border border-stone-200 bg-stone-100" style={{ height }}>
        <div ref={box} className="absolute inset-0" />
        {state === "ready" && (
          <>
            {/* The pin is fixed; the map moves under it. */}
            <MapPin className="absolute left-1/2 top-1/2 h-9 w-9 -translate-x-1/2 -translate-y-full text-terracotta fill-terracotta/30 drop-shadow pointer-events-none" />
            <div className="absolute left-1/2 top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-stoneWarm/70 pointer-events-none" />
            <div className="absolute top-2 left-1/2 -translate-x-1/2 rounded-full bg-stoneWarm/85 text-white text-[11px] px-3 py-1 pointer-events-none">
              {hint}
            </div>
          </>
        )}
        {state === "loading" && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-stone-500"><Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading map…</div>
        )}
        {state === "unavailable" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-sm text-stone-600 p-4" data-testid={`${testid}-unavailable`}>
            <MapPin className="h-6 w-6 text-stone-400 mb-1" />
            {value?.lat != null
              ? <>Location set ({value.lat.toFixed(5)}, {value.lng.toFixed(5)})</>
              : <>The map isn't available right now. Use your current location or pick an address from the search.</>}
          </div>
        )}
      </div>
      <Button type="button" variant="outline" size="sm" onClick={useCurrent} disabled={locating} className="rounded-full" data-testid={`${testid}-current`}>
        {locating ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <LocateFixed className="h-3.5 w-3.5 mr-1" />}
        Use my current location
      </Button>
    </div>
  );
}
